import React from "react";
import "./InterestsSelector.scss";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Chip } from "primereact/chip";
import {
  InterestsViewModel,
  SectorNode,
} from "@/features/settings/viewmodel/InterestsViewModel";

interface InterestsSelectorProps {
  selectedSectors?: number[];
  onSectorsChange?: (sectors: number[]) => void;
}

export const InterestsSelector: React.FC<InterestsSelectorProps> = ({
  selectedSectors,
  onSectorsChange,
}) => {
  const {
    searchQuery,
    onSearchQueryChange,
    expandedSectors,
    selectedSectors: selectedSectorsSet,
    selectedSectorNames,
    filteredSectorsTree,
    toggleSectorExpansion,
    toggleSectorSelection,
    removeSector,
  } = InterestsViewModel({ selectedSectors, onSectorsChange });

  // Render sectors tree (same structure as OrganizationInfoTab)
  const renderSectorsTree = (nodes: SectorNode[], depth = 0): JSX.Element => (
    <ul
      className="sector-tree"
      style={{
        marginLeft: depth ? 16 : 0,
        paddingLeft: 0,
        listStyle: "none",
        margin: 0,
      }}
    >
      {nodes.map((node) => {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedSectors.has(node.id);
        const isChecked = selectedSectorsSet.has(node.id);

        return (
          <li key={node.id} style={{ marginBottom: "0.25rem" }}>
            <div className="tree-row">
              {hasChildren ? (
                <button
                  type="button"
                  className="tree-toggle"
                  onClick={() => toggleSectorExpansion(node.id)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0.25rem",
                    marginRight: "0.5rem",
                    display: "flex",
                    alignItems: "center",
                    width: "24px",
                    height: "24px",
                  }}
                >
                  <i
                    className={`pi ${
                      isExpanded ? "pi-chevron-down" : "pi-chevron-right"
                    }`}
                    style={{ fontSize: "0.75rem", color: "#6B7280" }}
                  />
                </button>
              ) : (
                <span
                  style={{
                    width: "24px",
                    height: "24px",
                    display: "inline-block",
                    marginRight: "0.5rem",
                  }}
                />
              )}
              <Checkbox
                inputId={`sector-tree-${node.id}`}
                checked={isChecked}
                onChange={() => toggleSectorSelection(node.id)}
              />
              <label
                htmlFor={`sector-tree-${node.id}`}
                style={{
                  marginLeft: "0.5rem",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  color: "#374151",
                  flex: 1,
                  marginBottom: 0,
                }}
              >
                {node.label}
              </label>
            </div>
            {hasChildren &&
              isExpanded &&
              renderSectorsTree(node.children!, depth + 1)}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="form-row">
      <div className="form-group">
        <label>Interests</label>
        <div className="interests-selector">
          {/* Selected sectors chips */}
          {selectedSectorNames.length > 0 && (
            <div className="selected-chips p-2 mb-3 border-1 surface-border border-round">
              {selectedSectorNames.map((sector) => (
                <Chip
                  key={sector.id}
                  label={sector.name}
                  removable
                  onRemove={() => {
                    removeSector(sector.id);
                    return true;
                  }}
                  className="mr-2 mb-2"
                />
              ))}
            </div>
          )}

          {/* Search input */}
          <span className="p-input-icon-left w-full mb-3">
            <i className="pi pi-search" />
            <InputText
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              placeholder="Search for Sectors..."
              className="w-full"
            />
          </span>

          {/* Sectors tree list */}
          <div className="interests-list sectors-tree-wrap">
            {renderSectorsTree(filteredSectorsTree)}
          </div>
        </div>
      </div>
    </div>
  );
};
